import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { format, formatDistanceToNowStrict } from "date-fns";
import { CheckCircle2, Printer, MessageCircle, ArrowLeft, Link2, WifiOff, XCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "@/lib/api";
import { Loader } from "@/components/common/Primitives";
import { Invoice, whatsappShareText, waLink } from "@/components/orders/Invoice";
import { saveTrackedOrder } from "@/lib/orderTracking";
import { printInvoice } from "@/lib/printInvoice";

const STEPS = [["new", "Pending"], ["accepted", "Accepted"], ["preparing", "Preparing"], ["ready", "Ready"], ["completed", "Completed"]];
const TERMINAL = ["completed", "cancelled"];
const POLL_MS = 10000;
const printA4 = () => printInvoice(document.querySelector(".sm-print-area"), "a4");


function useOrderPolling(orderId, token) {
  const [order, setOrder] = useState(null);
  const [fatal, setFatal] = useState("");
  const [offline, setOffline] = useState(false);
  const [checkedAt, setCheckedAt] = useState(null);
  const timer = useRef(null);
  const alive = useRef(true);
  const delay = useRef(POLL_MS);

  const load = useCallback(async () => {
    clearTimeout(timer.current);
    try {
      const { data } = await api.get(`/public/orders/${orderId}`, { params: { token } });
      if (!alive.current) return;
      setOrder(data); setOffline(false); setCheckedAt(new Date()); delay.current = POLL_MS;
      if (TERMINAL.includes(data.status)) return;
    } catch (e) {
      const code = e?.response?.status;
      if (code === 404 || code === 422) return setFatal("This tracking link is invalid or has expired.");
      setOffline(true);
      delay.current = Math.min(delay.current * 2, 60000);
    }
    if (alive.current && document.visibilityState === "visible") {
      clearTimeout(timer.current);
      timer.current = setTimeout(load, delay.current);
    }
  }, [orderId, token]);

  useEffect(() => {
    alive.current = true;
    if (!token) { setFatal("This tracking link is missing its security token."); return undefined; }
    load();
    const onVis = () => { if (document.visibilityState === "visible") load(); else clearTimeout(timer.current); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("online", load);
    return () => { alive.current = false; clearTimeout(timer.current); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("online", load); };
  }, [load, token]);

  return { order, fatal, offline, checkedAt, reload: load };
}

function Progress({ status }) {
  if (status === "cancelled") return (
    <div className="mt-6 flex items-center gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-rose-700" data-testid="order-cancelled">
      <XCircle className="h-6 w-6 shrink-0" /><div><p className="font-semibold">Order cancelled</p><p className="text-sm">Please contact the restaurant staff if you have questions.</p></div>
    </div>
  );
  const step = STEPS.findIndex(([s]) => s === status);
  return (
    <ol className="mt-6 flex justify-between gap-1" data-testid="order-progress">
      {STEPS.map(([s, label], i) => (
        <li key={s} className="flex-1 text-center" data-testid={`progress-step-${s}`} data-done={i <= step}>
          <div className={`h-1.5 rounded-full transition-colors duration-500 ${i <= step ? "bg-emerald-500" : "bg-slate-200"}`} />
          <p className={`mt-1.5 text-[11px] sm:text-xs ${i === step ? "font-semibold text-emerald-700" : i < step ? "text-emerald-600" : "text-slate-400"}`}>{label}</p>
        </li>
      ))}
    </ol>
  );
}

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const { order, fatal, offline, checkedAt, reload } = useOrderPolling(orderId, token);
  const [, tick] = useState(0);

  useEffect(() => { toast.dismiss(); const t = setInterval(() => tick((n) => n + 1), 15000); return () => clearInterval(t); }, []);
  useEffect(() => {
    if (order) saveTrackedOrder({ id: order.id, token, number: order.order_number, slug: order.restaurant.slug, at: order.created_at });
  }, [order?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (fatal) return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center" data-testid="order-error">
      <XCircle className="h-10 w-10 text-slate-300" /><p className="mt-4 font-heading text-lg font-semibold text-slate-800">{fatal}</p>
    </div>
  );
  if (!order) return <Loader label="Loading your order…" />;

  const r = order.restaurant;
  const label = order.status === "cancelled" ? "Cancelled" : STEPS.find(([s]) => s === order.status)?.[1] || order.status;
  const fresh = Date.now() - new Date(order.created_at).getTime() < 10 * 60000;
  const copy = () => navigator.clipboard.writeText(window.location.href)
    .then(() => toast.success("Tracking link copied"))
    .catch(() => toast.error("Couldn't copy automatically — copy the address from your browser bar"));
  const cancelled = order.status === "cancelled";

  return (
    <div className="min-h-screen bg-[#FAFAF7] px-4 py-8">
      <div className="mx-auto max-w-2xl">
        <div className="no-print sm-fade-up text-center">
          {cancelled ? <XCircle className="mx-auto h-14 w-14 text-rose-500" /> : <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />}
          <h1 className="mt-3 font-heading text-3xl font-bold text-slate-900" data-testid="confirm-title">{cancelled ? "Order cancelled" : fresh ? "Order placed!" : "Track your order"}</h1>
          <p className="mt-1 text-slate-500">Order <b data-testid="confirm-order-number">{order.order_number}</b> at <b data-testid="confirm-restaurant">{r.name}</b>
            {order.table_name && <> · <span data-testid="confirm-table">{order.table_name}</span></>}</p>
        </div>

        <div className="no-print mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="order-status-card">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-slate-500">Status: <span className="font-heading text-lg font-bold text-slate-900" data-testid="order-status-label">{label}</span></p>
            <p className="text-xs text-slate-400" data-testid="order-last-updated">
              Last updated {format(new Date(order.status_updated_at), "hh:mm a, dd MMM")}
            </p>
          </div>
          <Progress status={order.status} />
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-400">
            {offline ? (
              <span className="flex items-center gap-1.5 text-amber-600" data-testid="order-offline"><WifiOff className="h-3.5 w-3.5" />Connection lost — showing last known status, retrying…</span>
            ) : TERMINAL.includes(order.status) ? <span>This order is closed.</span> : (
              <span data-testid="order-polling-note">Checks for updates every 10 seconds{checkedAt && ` · checked ${formatDistanceToNowStrict(checkedAt)} ago`}</span>
            )}
            <button onClick={reload} className="flex items-center gap-1 font-medium text-emerald-600" data-testid="order-refresh-btn"><RefreshCw className="h-3.5 w-3.5" />Refresh</button>
          </div>
        </div>

        <div className="no-print mt-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="order-summary">
          {order.items.map((i, idx) => (
            <div key={idx} className="flex justify-between py-1 text-sm"><span>{i.quantity} × {i.name}</span><span>{money(i.line_total)}</span></div>
          ))}
          <div className="mt-2 flex justify-between border-t border-slate-100 pt-2 font-heading font-bold text-slate-900"><span>Total</span><span data-testid="order-total">{money(order.grand_total)}</span></div>
        </div>

        <div className="no-print mt-6 flex flex-wrap justify-center gap-2">
          <button onClick={copy} className="inline-flex items-center rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white" data-testid="copy-track-link-btn"><Link2 className="mr-2 h-4 w-4" />Copy tracking link</button>
          <button onClick={printA4} className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium" data-testid="confirm-print-btn"><Printer className="mr-2 h-4 w-4" />Print / Save PDF</button>
          {r.whatsapp?.enabled && r.whatsapp?.number && (
            <a href={waLink(whatsappShareText(order), r.whatsapp.number)} target="_blank" rel="noreferrer" data-testid="confirm-whatsapp-btn"
              className="inline-flex items-center rounded-full bg-[#25D366] px-4 py-2 text-sm font-medium text-white"><MessageCircle className="mr-2 h-4 w-4" />Send to restaurant</a>
          )}
        </div>

        <div className="sm-print-area mt-6 overflow-hidden rounded-2xl border border-slate-200 shadow-sm"><Invoice order={order} /></div>
        <Link to={`/r/${r.slug}${order.table_id ? `?table=${order.table_id}` : ""}`} className="no-print mt-6 flex items-center justify-center gap-1 text-sm font-medium text-emerald-600" data-testid="back-to-menu"><ArrowLeft className="h-4 w-4" />Back to menu</Link>
        <p className="no-print mt-2 text-center text-xs text-slate-400">You can reopen this order any time from “Track your order” on the menu, or with the copied link.</p>
      </div>
    </div>
  );
}
